import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'found';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import getMuiIcon from '../../utils/getMuiIcon';
import { borderRadius, breakpoints, palette, spaces } from '../../assets/styles/themes/tokens';
import { ampliTrackTrialReportCardMessageDisplayedEvent } from '../../services/track.service';
import { COMPLETE_AREA_GRADE, formatGradeValue, isPerfectGrade } from './grade';
import { reportCardAreas } from './reportCard.config';
import { TrialReportCardProgress } from './useTrialReportCardProgress';

interface TrialReportCardDialogProps {
  open: boolean;
  onClose: () => void;
  firstName?: string;
  progress: TrialReportCardProgress;
}

// A completed area always scores the best grade (1,0).
const COMPLETED_AREA_LABEL = formatGradeValue(COMPLETE_AREA_GRADE);

const paperStyles = {
  borderRadius: borderRadius.md,
  width: '100%',
  maxWidth: '420px',
  // On phones, use 16px side gutters instead of MUI's default 32px paper margin
  // so the dialog uses more of the narrow screen.
  [`@media (max-width:${breakpoints.mobile}px)`]: {
    marginLeft: spaces.md,
    marginRight: spaces.md,
  },
};

const titleStyles = {
  position: 'relative',
  padding: spaces.lg,
  paddingBottom: spaces.xs,
  textAlign: 'center',
};

const closeButtonStyles = {
  position: 'absolute',
  top: spaces.xs,
  right: spaces.xs,
  padding: spaces.xxs,
  color: palette.text.secondary,
  '&:hover': { color: palette.text.primary },
};

const headingWrapperStyles = {
  position: 'relative',
  display: 'inline-block',
  paddingBottom: '4px',
};

const headingUnderlineStyles = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: '3px',
  borderRadius: '3px',
  backgroundColor: palette.accent.pink,
  transform: 'rotate(-0.8deg)',
};

const rowStyles = {
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: spaces.sm,
  paddingY: spaces.sm,
  borderTop: `1px solid ${palette.elevation.outlined}`,
};

const areaLabelStyles = {
  fontWeight: 600,
  color: palette.text.primary,
};

const stateStyles = {
  fontWeight: 700,
  // A completed area always scores 1,0, which the design colours green.
  color: palette.success.main,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const ungradedStateStyles = {
  fontStyle: 'italic',
  color: palette.text.secondary,
  whiteSpace: 'nowrap',
};

const totalRowStyles = {
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginTop: spaces.xxs,
  paddingTop: spaces.sm,
  borderTop: `2px solid ${palette.text.primary}`,
};

const totalLabelStyles = {
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: palette.text.secondary,
};

const totalValueStyles = {
  fontVariantNumeric: 'tabular-nums',
};

const TrialReportCardDialog: React.FC<TrialReportCardDialogProps> = ({
  open,
  onClose,
  firstName,
  progress,
}) => {
  const { t } = useTranslation();
  const { completionByArea, grade } = progress;
  const ungradedLabel = t('Not graded');
  // Overall grade is blue (the in-app accent) until every area is done, then green.
  const totalValueColor = isPerfectGrade(grade) ? palette.success.main : palette.primary.main;

  useEffect(() => {
    if (open) {
      ampliTrackTrialReportCardMessageDisplayedEvent();
    }
    // Fire once per open; progress changes while the dialog is open must not
    // re-fire the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} data-testid='trial-report-card-dialog' PaperProps={{ sx: paperStyles }}>
      <DialogTitle component='div' sx={titleStyles}>
        <IconButton onClick={onClose} aria-label={t('Close')} sx={closeButtonStyles}>
          <SvgIcon component={getMuiIcon('close')} sx={{ color: 'inherit' }} aria-hidden='true' />
        </IconButton>
        <Box sx={headingWrapperStyles}>
          <Typography variant='h5' sx={{ color: palette.text.primary }}>
            {t('Your Report Card')}
          </Typography>
          <Box sx={headingUnderlineStyles} />
        </Box>
        <Typography variant='body2' sx={{ marginTop: spaces.sm, color: palette.text.secondary }}>
          {t('School Year 2026 / 2027 · {{firstName}}', { firstName })}
        </Typography>
        <Typography variant='body2' sx={{ marginTop: spaces.xs, color: palette.text.secondary }}>
          { !isPerfectGrade(grade) && t('Try our key features and work your way toward a perfect grade.')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: spaces.lg, paddingTop: 0 }}>
        {reportCardAreas.map((area) => {
          const isComplete = completionByArea[area.id];
          return (
            <Stack
              key={area.id}
              direction='row'
              sx={rowStyles}
              data-testid={`trial-report-card-row-${area.id}`}
            >
              <Typography variant='subtitle1' sx={areaLabelStyles}>
                {t(area.labelKey)}
              </Typography>
              {isComplete ? (
                <Typography variant='subtitle1' sx={stateStyles}>
                  {COMPLETED_AREA_LABEL}
                </Typography>
              ) : (
                <Stack direction='row' spacing={spaces.sm} alignItems='center'>
                  <Typography variant='body2' sx={ungradedStateStyles}>
                    {ungradedLabel}
                  </Typography>
                  <Button
                    variant='contained'
                    size='small'
                    color='primary'
                    component={RouterLink}
                    to={area.ctaRoute}
                    onClick={onClose}
                  >
                    {t('Get started')}
                  </Button>
                </Stack>
              )}
            </Stack>
          );
        })}
        <Stack direction='row' sx={totalRowStyles}>
          <Typography variant='overline' sx={totalLabelStyles}>
            {t('Overall Grade')}
          </Typography>
          {grade.label ? (
            <Typography variant='h4' sx={{ ...totalValueStyles, color: totalValueColor }}>{grade.label}</Typography>
          ) : (
            <Typography variant='body2' sx={ungradedStateStyles}>
              {ungradedLabel}
            </Typography>
          )}
        </Stack>
        {isPerfectGrade(grade) && (
          <Typography variant='body2' sx={{ marginTop: spaces.sm, color: palette.text.secondary }}>
            {/* eslint-disable-next-line max-len */}
            {t('Top marks. You now know how to find and download the right teaching materials, create your own content with Muki, and keep everything organized in collections. Overall assessment: passed with flying colors. Keep it up.')}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrialReportCardDialog;
